/**
 * Application-specific wrapper for ProtectedRoute component
 * 
 * This wrapper provides a convenient way to use the library's ProtectedRoute
 * by automatically wiring up authentication and user state from hooks.
 * 
 * Usage:
 * ```typescript
 * import { ProtectedRoute } from '@/components/ProtectedRoute'
 * 
 * <ProtectedRoute requiredScopes={['admin']}>
 *   <AdminComponent />
 * </ProtectedRoute>
 * ```
 */

import React from 'react'
import type { JSX } from 'react'
import { ProtectedRoute as LibraryProtectedRoute } from '@revalize/auth/frontend'
import { useAuth } from '@/hooks/useAppAuth'
import { useApi } from '@/hooks/useAppApi'
import { usePermissions } from '@/hooks/usePermissions'

/**
 * Props for ProtectedRoute wrapper component
 */
interface ProtectedRouteProps {
  /** Child components to render when access is granted */
  children: React.ReactNode
  /** Array of scopes required to access this route (legacy: OR matching) */
  requiredScopes?: string[]
  /** Array of permissions required (wildcard-aware, OR matching) */
  requiredPermissions?: string[]
  /** Path to redirect to when user is not authenticated (default: '/login') */
  loginPath?: string
  /** Path to redirect to when user lacks required permissions (default: '/unauthorized') */
  unauthorizedPath?: string
  /** Custom loading component (optional) */
  loadingComponent?: React.ReactNode
}

/**
 * Wrapper component that automatically wires up authentication state
 * 
 * @example Basic authentication protection
 * ```tsx
 * <ProtectedRoute>
 *   <DashboardComponent />
 * </ProtectedRoute>
 * ```
 * 
 * @example Role-based protection
 * ```tsx
 * <ProtectedRoute requiredScopes={['admin']}>
 *   <AdminPanelComponent />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute = ({
  children,
  requiredScopes,
  requiredPermissions,
  loginPath,
  unauthorizedPath,
  loadingComponent,
}: ProtectedRouteProps): JSX.Element => {
  const { isLoading, isAuthenticated, error, signoutRedirect } = useAuth()
  const { user, isInitializing, authError } = useApi()
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions()

  // Build props object with explicit undefined handling for exactOptionalPropertyTypes
  const libraryProps: React.ComponentProps<typeof LibraryProtectedRoute> = {
    isAuthenticated,
    user,
    children,
  }

  if (isLoading !== undefined) libraryProps.isLoading = isLoading || permissionsLoading
  // Surface token exchange errors (authError) alongside OIDC errors
  const combinedError = error || authError || null
  if (combinedError !== undefined) libraryProps.error = combinedError

  // When auth fails (e.g., user not provisioned), clear the Cognito session
  // to prevent an infinite redirect loop between login and the OIDC callback
  libraryProps.onAuthError = (): void => {
    signoutRedirect()
  }
  if (isInitializing !== undefined) libraryProps.isInitializing = isInitializing
  if (requiredScopes !== undefined) libraryProps.requiredScopes = requiredScopes
  if (loginPath !== undefined) libraryProps.loginPath = loginPath
  if (unauthorizedPath !== undefined) libraryProps.unauthorizedPath = unauthorizedPath
  if (loadingComponent !== undefined) libraryProps.loadingComponent = loadingComponent

  // Permission check using expanded permissions (JWT scopes + DB permissions)
  // This enables wildcard matching: user with '*:*' can access routes requiring 'admin:*'
  if (requiredPermissions && user && !isInitializing && !permissionsLoading) {
    if (!hasAnyPermission(requiredPermissions)) {
      // User authenticated but lacks permissions - library will handle redirect
      libraryProps.requiredScopes = ['__permission_denied__']
    }
  }

  return <LibraryProtectedRoute {...libraryProps} />
}

// Re-export useRoleCheck from library for convenience
export { useRoleCheck, withProtectedRoute } from '@revalize/auth/frontend'

